import React, { Component } from 'react';
import { isNil, isEqual, get, first, memoize } from 'lodash';

import { Overlay } from 'src/common-ui/index';
import { WorklistProps, WorklistState } from 'src/pages/Worklist/Worklist.types';
import CompanionList from 'src/components/CompanionList/CompanionList';
import WorklistViewContainer from 'src/pages/Worklist/WorklistViewContainer';
import { parseCompanionListViewConfig } from 'src/utils/Component/ListView';
import { TenantConfigViewData } from 'src/dao/tenantConfigClient';
import { worklistCompanionViewContainer, worklistContainer } from './Worklist.styles';
import { FabType } from 'src/components/higherOrder/withFab';
import { Route, Routes } from 'react-router-dom';
import { GroupViewItem } from 'configuration-schemas/viewdefns/general';
import { SortByDirection } from 'src/components/Subheader/Subheader.types';
import { companionDataParse } from 'src/components/ListGridPair/ListGridPair.utils';
import { NoSelectedCompanionItemOverlay } from 'src/components/NoSelectedCompanionItemOverlay/NoSelectedCompanionItemOverlay';
import { topMemInUrl } from 'src/utils/Functions/epicsFunctions';
import { ListViewable } from 'src/common-ui/components/CompanionListView/CompanionListView';

export default class Worklist extends Component<WorklistProps, WorklistState> {
  constructor(props: WorklistProps) {
    super(props);

    this.state = {
      useStyleColors: true,
    };
  }

  componentDidMount() {
    this.props.onShowView();
  }

  componentDidUpdate(prevProps: WorklistProps) {
    if (!isEqual(this.props.styles, prevProps.styles) && this.props.config) {
      const { companionData } = this.generateCompanionData();
      const selectedCompanionItem =
        companionData.find((s) => s.id === this.props.selectedItemId) ?? first(companionData);

      if (!isNil(selectedCompanionItem) && selectedCompanionItem.id != this.props.selectedItemId) {
        this.props.selectItem(selectedCompanionItem.id);
      }
    }
  }

  generateCompanionData = () => {
    const { config, styles, companionSortField, companionSortDirection } = this.props;
    const defaultSortDataIndex = config?.companionView?.sortBy.defaults.dataIndex ?? '';
    const sortOptions = config?.companionView?.sortBy.options ?? [];
    const fieldExists = companionSortField !== null && sortOptions.some((opt) => opt.dataIndex === companionSortField);
    const effectiveSortField = fieldExists ? companionSortField! : defaultSortDataIndex;
    const companionDataLookup = parseCompanionListViewConfig(
      config?.companionView.itemMappings as unknown as TenantConfigViewData,
    );
    const companionData = companionDataParse(styles, companionDataLookup, companionSortDirection, effectiveSortField);
    return { companionData, companionDataLookup };
  };

  onFabClick = () => {
    switch (this.props.fab.fabType) {
      case FabType.planning:
        this.props.updateAssortmentPlan();
        break;
      default:
        break;
    }
  };

  generateScrollTo = memoize(
    () => {
      const levelField = this.props.selectedLevel?.dataIndex;
      const selectedItemId = this.props.selectedItemId;
      return {
        eventId: Date.now(),
        where: {
          key: levelField ? `${levelField}:id` : 'id',
          value: selectedItemId,
        },
      };
    },
    () => {
      const levelField = this.props.selectedLevel?.dataIndex;
      const selectedItemId = this.props.selectedItemId;
      return `${levelField}-${selectedItemId}`;
    },
  );

  handleSortChange = (sortByDataIndex: string) => {
    this.props.updateCompanionSortField(sortByDataIndex);
  };

  handleSortDirectionChange = (sortByDirection: SortByDirection) => {
    this.props.updateCompanionSortDirection(sortByDirection);
  };

  handleLevelChange = (levelField: string) => {
    const { config } = this.props;
    const levelBys: GroupViewItem[] = get(config, 'companionView.levelBy.view', []);
    const selectedLevel = levelBys.find((i) => i.dataIndex === levelField);
    if (selectedLevel == null) return; // this is error state.
    if (selectedLevel.dataIndex !== this.props.selectedLevel?.dataIndex) {
      this.props.selectLevel(selectedLevel);
    }
  };

  render() {
    const {
      isLoading,
      config,
      subheaderSlice,
      subheaderViewDefns,
      styles,
      flowStatus,
      updateFlowStatus,
      flowStatusConfig,
      showFlowStatus,
      onShowView,
      updateCompanionSearchString,
      updateStyleItem,
      companionSearch,
      adornments,
      showUndoBtn,
      isPrintMode,
      hideCompanion,
      selectedLevel,
      selectedItemId,
      isCompanionCollapsed,
      onToggleCompanion,
      companionSortField,
      companionSortDirection,
    } = this.props;

    // If we don't have config, there literally nothing to render.
    if (isNil(config)) {
      return <Overlay type="loading" visible={true} />;
    }

    const defaultTabIndex = config.tabs.findIndex((tab) => (tab.componentType as string) === config.defaultTab);
    const { companionData, companionDataLookup } = this.generateCompanionData();

    // If `hideCompanion` is true, then make a fake companion item to send into `Worklist`.
    // `Worklist` uses this as it's companion view selection.  It is expected that when the companion view is hidden,
    // the view is being used as a breadcrumb view and the topMember must be sent in from the url
    const topMem = topMemInUrl();
    const selectedCompanionItem: ListViewable | undefined = this.props.hideCompanion
      ? {
          id: topMem ?? 'HIDE_COMPANION_NO_TOP_MEMBER',
          name: 'HIDE_COMPANION_NOT_REAL_NAME',
          stars: null,
        }
      : companionData.find((item) => item.id === selectedItemId);

    const selectedListDataItem = selectedItemId
      ? styles.find((s) => s[companionDataLookup.titleId] == selectedItemId)
      : undefined;
    const scrollTo = this.generateScrollTo();

    let mainView: JSX.Element;

    if (isNil(selectedCompanionItem)) {
      mainView = <div style={{ width: '100%' }}>{isLoading ? null : <NoSelectedCompanionItemOverlay />}</div>;
    } else {
      mainView = (
        <WorklistViewContainer
          adornments={adornments}
          defaultTabIndex={defaultTabIndex >= 0 ? defaultTabIndex : 0}
          tabsConfig={config.tabs}
          selectedCompanionItem={selectedCompanionItem}
          selectedListDataItem={selectedListDataItem}
          subheaderSlice={subheaderSlice}
          subheaderViewDefns={subheaderViewDefns}
          styles={styles}
          onViewRefresh={onShowView}
          onStyleItemUpdate={updateStyleItem}
          showUndoBtn={showUndoBtn}
          isPrintMode={isPrintMode}
          // These are here so that in some instance we can inject or hide a global worklist title
          // Note that this can potentially conflict with the individual component's titles, as some components
          // support their own title, and some don't.
          // This is here to allow a global override, but in the case the subcomponents have a title, this won't work well
          overrideTitle={this.props.title}
          overrideHideTitle={this.props.hideTitle}
        />
      );
    }
    return (
      <div className={worklistContainer}>
        <Overlay type="loading" visible={isLoading || isNil(config)} />
        <div className="data-container" data-qa="worklist-view">
          {/* `hideCompanion` implemented in css, as otherwise it interferes with the data fetch */}
          <div className={worklistCompanionViewContainer(hideCompanion)} data-qa="worklist-view-companion">
            {hideCompanion ? (
              <div data-qa="worklist-view-hide-companion" style={{ display: 'none' }} />
            ) : (
              <CompanionList
                config={config.companionView}
                selectedItemId={selectedItemId ?? null}
                data={hideCompanion ? [] : companionData}
                dataLookup={companionDataLookup}
                isDataloaded={hideCompanion || !isLoading}
                renderSearchComponent={true}
                renderFilterComponent={showFlowStatus}
                sortDirection={companionSortDirection}
                sortField={
                  companionSortField !== null &&
                  (config?.companionView?.sortBy?.options ?? []).some((opt) => opt.dataIndex === companionSortField)
                    ? companionSortField!
                    : (config?.companionView?.sortBy.defaults.dataIndex ?? '')
                }
                levelField={selectedLevel?.dataIndex}
                search={companionSearch}
                flowStatus={flowStatus}
                flowStatusOptions={flowStatusConfig}
                scrollTo={scrollTo}
                onSearchChange={updateCompanionSearchString}
                onItemSelect={this.props.selectItem}
                onLevelChange={this.handleLevelChange}
                onSortChange={this.handleSortChange}
                onSortDirectionChange={this.handleSortDirectionChange}
                onFilterChange={updateFlowStatus}
                isCompanionCollapsed={isCompanionCollapsed}
                onToggleCompanion={onToggleCompanion}
              />
            )}
          </div>
          <Routes>
            {config.tabs.map((tab) => (
              <Route key={tab.pathSlot} path={`${tab.pathSlot}`} element={mainView} />
            ))}
          </Routes>
        </div>
      </div>
    );
  }
}
